import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiUserCheck, FiDollarSign, FiActivity } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalTrainers: 0,
    todayAttendance: 0,
    monthlyRevenue: 0,
    expiringSoon: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Prevent duplicate fetches on mount
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch dashboard statistics
      const [membersRes, trainersRes, revenueRes] = await Promise.all([
        api.get('/members?page=1&limit=1'),
        api.get('/trainers?page=1&limit=1'),
        api.get('/payments/revenue/monthly'),
      ]);

      const memberData = membersRes.data?.data || {};
      const trainerData = trainersRes.data?.data || {};
      const revenueData = revenueRes.data?.data || {};

      setStats({
        totalMembers: memberData.pagination?.total || 0,
        activeMembers: memberData.pagination?.total || 0,
        totalTrainers: trainerData.pagination?.total || 0,
        todayAttendance: 0, // Would come from attendance endpoint
        monthlyRevenue: revenueData.revenue || 0,
        expiringSoon: 0, // Would come from memberships endpoint
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Don't show error toast if it's just empty data
      if (error.response?.status !== 404) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard Overview</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Members"
          value={stats.totalMembers}
          icon={FiUsers}
          color="bg-blue-500"
          subtitle={`${stats.activeMembers} active`}
        />
        <StatCard
          title="Total Trainers"
          value={stats.totalTrainers}
          icon={FiUserCheck}
          color="bg-green-500"
        />
        <StatCard
          title="Today's Attendance"
          value={stats.todayAttendance}
          icon={FiActivity}
          color="bg-purple-500"
        />
        <StatCard
          title="Monthly Revenue"
          value={`₹${stats.monthlyRevenue.toLocaleString()}`}
          icon={FiDollarSign}
          color="bg-yellow-500"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/members/new')}
              className="w-full text-left px-4 py-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            >
              + Add New Member
            </button>
            <button className="w-full text-left px-4 py-3 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors">
              + Add New Trainer
            </button>
            <button className="w-full text-left px-4 py-3 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors">
              + Record Payment
            </button>
            <button className="w-full text-left px-4 py-3 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors">
              + Create Class
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-4">
            <p className="text-gray-600 text-center py-8">No recent activity</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {stats.expiringSoon > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            <strong>Note:</strong> {stats.expiringSoon} membership(s) expiring soon
          </p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
